import { useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import { PageLoading } from '../../components/ui';
import type { DistributorDashboard as DashboardData } from '../../types';

/**
 * The distributor's landing page — a glanceable summary of their territory.
 *
 * Shows the numbers that matter to a distributor: how many retailers
 * they serve, orders flowing through their area, and their own primary
 * orders to the company.
 */
export default function DistributorDashboard() {
  const { user } = useAuth();
  const toast = useToast();
  const [data, setData] = useState<DashboardData | null>(null);

  useEffect(() => {
    api.get<DashboardData>('/distributor/dashboard')
      .then(setData)
      .catch((err) => {
        toast.error(err instanceof ApiClientError ? err.message : 'Could not load dashboard.');
      });
  }, [toast]);

  return (
    <>
      <header className="topbar">
        <div>
          <h1>Welcome, {user?.fullName?.split(' ')[0] ?? 'Distributor'}</h1>
          <p className="topbar-sub">Here is how your territory looks today.</p>
        </div>
      </header>

      <div className="page">
        {!data ? <PageLoading /> : (
          <div className="col gap-24">
            {/* Top-level stats */}
            <div className="stat-row">
              <StatCard icon="users" label="Retailers" value={data.retailerCount} />
              <StatCard icon="list" label="Pending orders" value={data.secondary.pendingCount} />
              <StatCard icon="list" label="Dispatched today" value={data.secondary.dispatchedToday} />
              <StatCard icon="list" label="Primary orders" value={data.primary.totalThisMonth} />
            </div>

            {/* Monthly summary cards */}
            <div className="row gap-16" style={{ flexWrap: 'wrap' }}>
              <div className="card card-pad" style={{ flex: '1 1 240px' }}>
                <h4 style={{ margin: '0 0 8px' }}>Secondary orders this month</h4>
                <div className="col gap-4">
                  <div className="row between"><span className="small faint">Total orders</span><span>{data.secondary.totalThisMonth}</span></div>
                  <div className="row between"><span className="small faint">Total value</span><span className="primary">₹{(data.secondary.totalValue / 100).toLocaleString('en-IN')}</span></div>
                </div>
              </div>
              <div className="card card-pad" style={{ flex: '1 1 240px' }}>
                <h4 style={{ margin: '0 0 8px' }}>Primary orders this month</h4>
                <div className="col gap-4">
                  <div className="row between"><span className="small faint">Total orders</span><span>{data.primary.totalThisMonth}</span></div>
                  <div className="row between"><span className="small faint">Total value</span><span className="primary">₹{(data.primary.totalValue / 100).toLocaleString('en-IN')}</span></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function StatCard({ icon, label, value }: { icon: 'map' | 'users' | 'list'; label: string; value: number }) {
  return (
    <div className="stat">
      <div className="stat-icon"><Icon name={icon} size={18} /></div>
      <div>
        <div className="stat-label">{label}</div>
        <div className="stat-value">{value}</div>
      </div>
    </div>
  );
}
